import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { AddressSearchInput } from "@/components/AddressSearchInput";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { AlertTriangle, BrainCircuit, Clock, Mail, Phone, Search, User, UserPlus } from "lucide-react";
import { CUSTOMER_ACTIVITY_PRESENTATION } from "@shared/customerRecentActivity";
import { useState, useEffect, useCallback, useRef } from "react";
import { formatDate } from "@/lib/dateUtils";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { usePortalMode } from "@/contexts/PortalModeContext";
import { normaliseCustomerDisplayName } from "@shared/customerName";
import { CustomerSearchResult } from "@/components/customers/CustomerSearchResult";
import { CustomerCreateOverride } from "@/components/customers/CustomerCreateOverride";
import { isFullCustomerSearchHit, toFullCustomerSearchHit } from "@shared/customerSearchAccess";
import {
  customerDuplicateCheckCandidates,
  customerDuplicateCheckRequiresReview,
  isCustomerCreateDuplicateReviewMessage,
} from "@shared/customerCreationDuplicateReview";
import ReportIssueButton from "@/components/errors/ReportIssueButton";
import { keepPreviousData } from "@tanstack/react-query";

const EMPTY_FORM = {
  name: "",
  contact: "",
  email: "",
  address: "",
  unitNumber: "",
  postalCode: "",
  notes: "",
  isCondo: false,
};

function normalisePhoneForCompare(raw: string | null | undefined): string {
  const digits = (raw ?? "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("65") && digits.length >= 10) return digits.slice(2);
  return digits;
}

export default function Customers() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { isSales } = usePortalMode();
  const customerPath = (cid: number) => (isSales ? `/sales/customers/${cid}` : `/customers/id/${cid}`);
  const utils = trpc.useUtils();
  const claimCustomer = trpc.customers.claimCustomer.useMutation({
    onSuccess: () => {
      toast.success("Customer claimed — you are now the PIC.");
      utils.customers.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [picId, setPicId] = useState<number | undefined>(undefined);
  const [addressQuery, setAddressQuery] = useState("");

  // Fetch sales staff for PIC dropdown
  const { data: salesStaff = [] } = trpc.customers.listSalesStaff.useQuery();

  // Default PIC to logged-in user when dialog opens or user loads
  useEffect(() => {
    if (showAdd && user?.id && !picId) {
      setPicId(user.id);
    }
  }, [showAdd, user?.id]);
  // Duplicate check state
  const [dupCheckName, setDupCheckName] = useState("");
  const [dupCheckContact, setDupCheckContact] = useState("");
  const [dupCheckEmail, setDupCheckEmail] = useState("");
  const [dupCheckAddress, setDupCheckAddress] = useState("");
  const [dupCheckUnitNumber, setDupCheckUnitNumber] = useState("");
  const [dupCheckPostalCode, setDupCheckPostalCode] = useState("");
  const [confirmedNoDup, setConfirmedNoDup] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const { data: recentCustomers = [], isLoading: loadingRecent, isError: recentActivityFailed, refetch: refetchRecent } = trpc.customers.recentlyActive.useQuery({}, {
    refetchOnMount: "always", refetchOnWindowFocus: true, staleTime: 0,
  });
  const { data: allCustomers, isLoading: loadingAll } = trpc.customers.list.useQuery({});
  const {
    data: searchResults,
    isLoading: loadingSearch,
    isFetching: fetchingSearch,
    isError: searchFailed,
    error: searchError,
    refetch: refetchSearch,
  } = trpc.customers.search.useQuery(
    { query: search },
    { enabled: search.length > 0, placeholderData: keepPreviousData }
  );
  const customers = search.length > 0 ? searchResults : allCustomers;
  const isLoading = search.length > 0 ? loadingSearch : loadingAll;

  const duplicateReviewRef = useRef<HTMLDivElement | null>(null);

  // Duplicate check — fires when name or contact is stable (debounced via state)
  const {
    data: duplicateCheck,
    isFetching: fetchingDuplicates,
    isError: duplicateCheckFailed,
  } = trpc.customers.checkDuplicates.useQuery(
    {
      name: dupCheckName,
      contact: dupCheckContact || undefined,
      email: dupCheckEmail || undefined,
      address: dupCheckAddress || undefined,
      unitNumber: dupCheckUnitNumber || undefined,
      postalCode: dupCheckPostalCode || undefined,
    },
    {
      enabled:
        dupCheckName.length > 1 ||
        dupCheckContact.length > 6 ||
        dupCheckEmail.length > 3 ||
        dupCheckAddress.length > 4 ||
        dupCheckPostalCode.length > 3,
    }
  );
  const dupMatches = customerDuplicateCheckCandidates(duplicateCheck);
  const requiresDuplicateReview = customerDuplicateCheckRequiresReview(duplicateCheck);

  // Debounce: update dupCheck fields 600ms after user stops typing
  useEffect(() => {
    const t = setTimeout(() => {
      setDupCheckName(normaliseCustomerDisplayName(form.name));
      setDupCheckContact(form.contact.trim());
      setDupCheckEmail(form.email.trim());
      setDupCheckAddress(form.address.trim());
      setDupCheckUnitNumber(form.unitNumber.trim());
      setDupCheckPostalCode(form.postalCode.trim());
    }, 600);
    return () => clearTimeout(t);
  }, [form.name, form.contact, form.email, form.address, form.unitNumber, form.postalCode]);

  // Any identity change invalidates an earlier duplicate review.
  useEffect(() => {
    setConfirmedNoDup(false);
    setOverrideReason("");
  }, [form.name, form.contact, form.email, form.address, form.unitNumber, form.postalCode]);

  const createCustomer = trpc.customers.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Customer "${form.name}" created`);
      utils.customers.list.invalidate();
      setShowAdd(false);
      setForm(EMPTY_FORM);
      setAddressQuery("");
      setDupCheckName("");
      setDupCheckContact("");
      setDupCheckEmail("");
      setDupCheckAddress("");
      setDupCheckUnitNumber("");
      setDupCheckPostalCode("");
      setConfirmedNoDup(false);
      setLocation(customerPath(data.id));
    },
    onError: (e: { message: string }) => {
      if (isCustomerCreateDuplicateReviewMessage(e.message)) {
        duplicateReviewRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      toast.error(e.message);
    },
  });

  const dupCheckPending =
    showAdd &&
    (normaliseCustomerDisplayName(form.name) !== dupCheckName ||
      form.contact.trim() !== dupCheckContact ||
      form.email.trim() !== dupCheckEmail ||
      form.address.trim() !== dupCheckAddress ||
      form.unitNumber.trim() !== dupCheckUnitNumber ||
      form.postalCode.trim() !== dupCheckPostalCode ||
      fetchingDuplicates);

  const duplicateReviewBlocking = requiresDuplicateReview && !confirmedNoDup;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedName = normaliseCustomerDisplayName(form.name);
    if (!normalizedName) return;
    if (dupCheckPending || duplicateReviewBlocking) {
      duplicateReviewRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (requiresDuplicateReview && overrideReason.trim().length < 8) {
      duplicateReviewRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    createCustomer.mutate({
      name: normalizedName,
      contact: form.contact.trim() || undefined,
      email: form.email.trim() || undefined,
      address: form.address.trim() || undefined,
      unitNumber: form.unitNumber.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      notes: form.notes.trim() || undefined,
      salespersonId: picId,
      creationSource: "CUSTOMER_DIRECTORY",
      forceCreateNew: confirmedNoDup || undefined,
      reviewedCandidateIds: dupMatches.map((c) => c.id),
      forceCreateReason: overrideReason.trim() || undefined,
    });
  };

  const handleClose = useCallback(() => {
    setShowAdd(false);
    setForm(EMPTY_FORM);
    setPicId(undefined);
    setAddressQuery("");
    setDupCheckName("");
    setDupCheckContact("");
    setDupCheckEmail("");
    setDupCheckAddress("");
    setDupCheckUnitNumber("");
    setDupCheckPostalCode("");
    setConfirmedNoDup(false);
    setOverrideReason("");
  }, []);

  const handleUseExistingCustomer = useCallback(
    (c: { id: number }) => {
      handleClose();
      setLocation(customerPath(c.id));
    },
    [handleClose, setLocation],
  );

  const field = (
    label: string,
    key: keyof typeof EMPTY_FORM,
    opts?: { placeholder?: string; type?: string; required?: boolean }
  ) => (
    <div>
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
        {label}{opts?.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={opts?.type ?? "text"}
        value={form[key as keyof Omit<typeof form, 'isCondo'>]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        onBlur={() => {
          if (key === "name") {
            setForm((f) => ({ ...f, name: normaliseCustomerDisplayName(f.name) }));
          }
        }}
        placeholder={opts?.placeholder ?? ""}
        required={opts?.required}
        className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-none"
      />
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Search and manage customer records
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setLocation("/customers/intelligence")} className="gap-2" size="sm">
              <BrainCircuit className="h-4 w-4" />
              Customer Intelligence
            </Button>
            <Button onClick={() => setShowAdd(true)} className="gap-2" size="sm">
              <UserPlus className="h-4 w-4" />
              Add Customer
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by customer number, name, email or contact..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Recent Customers — only show when not actively searching */}
        {!search && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Recently Active</h2>
            </div>
            {recentActivityFailed ? <p role="alert" className="text-sm text-destructive">
              Could not refresh recent activity. <button type="button" className="underline" onClick={() => refetchRecent()}>Retry</button>
            </p> : null}
            {loadingRecent ? (
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : recentCustomers.length > 0 ? (
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {recentCustomers.slice(0, 16).map((c) => (
                  <button
                    key={c.id}
                    title={`${CUSTOMER_ACTIVITY_PRESENTATION[c.activityType].description} · ${formatDate(c.activityDate)}`}
                    onClick={() => setLocation(customerPath(c.id))}
                    className="aspect-square rounded-lg border border-border bg-card hover:bg-accent hover:border-accent-foreground/20 transition-all duration-150 flex flex-col items-center justify-center gap-1 p-2 text-center group shadow-sm hover:shadow-md"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center shrink-0 transition-colors">
                      <span className="text-xs font-bold text-gray-600">
                        {c.name.trim().charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <p className="text-[10px] font-medium leading-tight line-clamp-2 text-foreground w-full">
                      {c.name}
                    </p>
                    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${CUSTOMER_ACTIVITY_PRESENTATION[c.activityType].className}`}>
                      {CUSTOMER_ACTIVITY_PRESENTATION[c.activityType].label}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        )}

        {/* Customer Count */}
        {!isLoading && customers && customers.length > 0 && (
          <p className="text-xs text-muted-foreground -mt-2">
            {customers.length} customer{customers.length !== 1 ? "s" : ""}{search ? " matching" : ""}
          </p>
        )}

        {/* Customer List */}
        {search && searchFailed && searchResults && searchResults.length > 0 ? (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex flex-wrap items-center gap-2"
            data-testid="customer-search-stale-warning"
          >
            <span>Showing last successful results. Latest search could not be refreshed.</span>
            <Button type="button" size="sm" variant="outline" onClick={() => refetchSearch()}>
              Retry
            </Button>
            <ReportIssueButton
              surface="sales.customer_search"
              safeErrorCode={searchError?.data?.code ?? "INTERNAL_SERVER_ERROR"}
              priorDataRetained
            />
          </div>
        ) : null}

        {search && searchFailed && !searchResults ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="customer-search-error">
            <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">Customer search could not be completed</p>
            <p className="text-sm mt-1">Try again in a moment.</p>
            <div className="mt-4 flex justify-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => refetchSearch()}>
                Retry
              </Button>
              <ReportIssueButton
                surface="sales.customer_search"
                safeErrorCode={searchError?.data?.code ?? "INTERNAL_SERVER_ERROR"}
                priorDataRetained={false}
              />
            </div>
          </div>
        ) : isLoading || (search && fetchingSearch && !searchResults) ? (
          <div className="space-y-1">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : customers && customers.length > 0 ? (
          (() => {
            // Group by first letter
            const sorted = [...customers].sort((a, b) => a.name.localeCompare(b.name));
            const groups: Record<string, typeof sorted> = {};
            sorted.forEach(c => {
              const letter = c.name.trim().charAt(0).toUpperCase();
              const key = /[A-Z]/.test(letter) ? letter : "#";
              if (!groups[key]) groups[key] = [];
              groups[key].push(c);
            });
            const letters = Object.keys(groups).sort((a, b) => a === "#" ? 1 : b === "#" ? -1 : a.localeCompare(b));
            return (
              <div className="space-y-6">
                {/* Alphabet jump bar — only show when not searching */}
                {!search && (
                  <div className="flex flex-wrap gap-1">
                    {letters.map(letter => (
                      <button
                        key={letter}
                        onClick={() => document.getElementById(`alpha-${letter}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                        className="w-7 h-7 text-xs font-semibold rounded hover:bg-gray-100 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {letter}
                      </button>
                    ))}
                  </div>
                )}
                {/* Grouped list */}
                {letters.map(letter => (
                  <div key={letter} id={`alpha-${letter}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-bold text-muted-foreground tracking-widest uppercase w-6">{letter}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <div className="space-y-0.5">
                      {groups[letter].map(customer => {
                        const fullSearch = search.length > 0 && isFullCustomerSearchHit(customer as never);
                        return (
                        <button
                          key={customer.id}
                          onClick={() => setLocation(customerPath(customer.id))}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-gray-50 transition-colors text-left group"
                          data-testid={search ? "customer-search-full-row" : undefined}
                        >
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0 text-xs font-semibold text-gray-500 group-hover:bg-gray-200 transition-colors">
                            {customer.name.trim().charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            {search ? (
                              <CustomerSearchResult customer={fullSearch ? customer : { ...customer, access: "full" }} />
                            ) : (
                              <>
                                <p className="text-sm font-medium truncate">{customer.name}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {"contact" in customer && customer.contact && customer.contact !== "-" ? customer.contact : ""}
                                  {"contact" in customer && customer.contact && customer.contact !== "-" && "email" in customer && customer.email ? " · " : ""}
                                  {"email" in customer && customer.email ? customer.email : ""}
                                  {(!("contact" in customer) || !customer.contact || customer.contact === "-") && !("email" in customer && customer.email) ? "No contact info" : ""}
                                </p>
                              </>
                            )}
                          </div>
                          {"salespersonName" in customer && customer.salespersonName ? (
                            <span className="text-xs text-muted-foreground shrink-0 hidden sm:block">{customer.salespersonName}</span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                claimCustomer.mutate({ id: customer.id });
                              }}
                              disabled={claimCustomer.isPending}
                              className="shrink-0 text-xs font-medium text-blue-600 border border-blue-200 rounded px-2 py-0.5 hover:bg-blue-50 transition-colors hidden sm:block"
                            >
                              Claim
                            </button>
                          )}
                        </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()
        ) : (
          <div className="text-center py-16 text-muted-foreground">
            <User className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No customers found</p>
            <p className="text-sm mt-1">
              {search
                ? "Try a different search term"
                : "Click \"Add Customer\" to create your first customer record"}
            </p>
          </div>
        )}
      </div>

      {/* Add Customer Dialog */}
      <Dialog open={showAdd} onOpenChange={(open) => { if (!open) handleClose(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {field("Full Name / Company Name", "name", { required: true, placeholder: "e.g. Chewbacca Chan" })}

            <div className="grid grid-cols-2 gap-4">
              {field("Contact Number", "contact", { placeholder: "e.g. 91234567" })}
              {field("Email Address", "email", { type: "email", placeholder: "e.g. chan@email.com" })}
            </div>

            {/* Smart Address Search */}
            <AddressSearchInput
              value={addressQuery}
              onAddressSelect={(address, postalCode) => {
                setForm(f => ({
                  ...f,
                  address,
                  postalCode,
                }));
                setAddressQuery(address);
              }}
            />

            {/* Postal Code — auto-filled from address selection */}
            {form.postalCode && (
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 text-sm">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Postal Code</span>
                <span className="font-mono font-semibold text-foreground ml-auto">{form.postalCode}</span>
              </div>
            )}

            {/* Condo / Apartment Question */}
            {form.address && (
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                  Is this a condo or apartment building?
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, isCondo: true }))}
                    className={`flex-1 px-3 py-2 text-sm border transition-colors ${
                      form.isCondo
                        ? "border-black bg-black text-white"
                        : "border-gray-200 text-gray-700 hover:border-black"
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, isCondo: false }))}
                    className={`flex-1 px-3 py-2 text-sm border transition-colors ${
                      !form.isCondo && form.address
                        ? "border-black bg-black text-white"
                        : "border-gray-200 text-gray-700 hover:border-black"
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            )}

            {/* Unit Number - Only show if condo */}
            {form.isCondo && (
              <div>
                {field("Unit / Floor", "unitNumber", { placeholder: "e.g. #03-12" })}
              </div>
            )}

            {/* PIC / Salesperson */}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                Person In Charge (PIC)<span className="text-red-500 ml-0.5">*</span>
              </label>
              <select
                value={picId ?? ""}
                onChange={e => setPicId(e.target.value ? Number(e.target.value) : undefined)}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-none bg-white"
              >
                <option value="">— Select PIC —</option>
                {salesStaff.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.id === user?.id ? " (you)" : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                Notes
              </label>
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any additional notes about this customer..."
                rows={3}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-none resize-none"
              />
            </div>

            {dupMatches.length > 0 && (
              <div ref={duplicateReviewRef} data-testid="customer-create-duplicate-review">
                <CustomerCreateOverride
                  candidates={dupMatches.map(toFullCustomerSearchHit)}
                  reviewRequired={duplicateReviewBlocking}
                  onSelectExisting={(id) => handleUseExistingCustomer({ id })}
                  onConfirmCreateNew={(reason) => {
                    setOverrideReason(reason);
                    setConfirmedNoDup(true);
                  }}
                />
              </div>
            )}
            {duplicateCheckFailed ? (
              <p role="alert" className="text-xs text-amber-900">
                Could not preview existing customers. Creating still checks on the server.
              </p>
            ) : null}

            <DialogFooter className="gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 h-9 text-xs border border-gray-200 text-gray-600 hover:border-black hover:text-black transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="customer-create-submit"
                disabled={
                  createCustomer.isPending ||
                  !form.name.trim() ||
                  dupCheckPending ||
                  duplicateReviewBlocking
                }
                className="px-6 h-9 bg-black text-white text-xs tracking-[0.15em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createCustomer.isPending
                  ? "Creating…"
                  : dupCheckPending
                    ? "Checking…"
                    : duplicateReviewBlocking
                      ? "Review matches first"
                      : "Create Customer"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
